import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import AxiosService from "../../services/AxiosHelper";

// const baseURL = "http://localhost:9000";
const baseURL = process.env.REACT_APP_BASEURL;

// functions for category
export const getCategoryList = createAsyncThunk(
  "product/getCategoryList",
  async (businessId, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/category` + (businessId ? `?businessId=${businessId}` : ""),
        method: "get",
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch categories");
    }
  }
);
export const addProductCategory = createAsyncThunk(
  "product/addProductCategory",
  async (values, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "post",
        url: baseURL + `/category`,
        data: values,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to add category");
    }
  }
);
export const deleteCategory = createAsyncThunk(
  "product/deleteCategory",
  async (id, { rejectWithValue }) => {
    try {
      await AxiosService.AxiostRequest({
        method: "delete",
        url: baseURL + `/category/${id}`,
      });
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to delete category");
    }
  }
);
export const updateProductCategory = createAsyncThunk(
  "product/updateProductCategory",
  async (values, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "put",
        url: baseURL + `/category/${values.id}`,
        data: values,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Update failed");
    }
  }
);

// function for product
export const getProductList = createAsyncThunk(
  "product/getProductList",
  async (businessId, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/product` + (businessId ? `?business=${businessId}` : ""),
        method: "get",
      });
      return response.data.data.products;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch products");
    }
  }
);
export const getProductDetail = createAsyncThunk(
  "product/getProductDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/product/${id}`,
        method: "get",
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch product details");
    }
  }
);
export const addProduct = createAsyncThunk(
  "product/addProduct",
  async (values, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "post",
        url: baseURL + `/product`,
        data: values,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to add product");
    }
  }
);
export const updateProduct = createAsyncThunk(
  "product/updateProduct",
  async (values, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "put",
        url: baseURL + `/product/${values.id}`,
        data: values,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Update failed");
    }
  }
);
export const deleteProduct = createAsyncThunk(
  "product/deleteProduct",
  async (id, { rejectWithValue }) => {
    try {
      await AxiosService.AxiostRequest({
        method: "delete",
        url: baseURL + `/product/${id}`,
      });
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to delete product");
    }
  }
);
export const uploadProductImages = createAsyncThunk(
  "product/uploadProductImages",
  async ({ id, files }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append("images", file));
      const response = await AxiosService.AxiostRequest({
        method: "post",
        url: baseURL + `/product/${id}/upload-images`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Upload failed");
    }
  }
);
export const removeProductImage = createAsyncThunk(
  "product/removeProductImage",
  async ({ id, imageUrl }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "delete",
        url: baseURL + `/product/${id}/remove-image`,
        data: { imageUrl },
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Removal failed");
    }
  }
);
const productSlice = createSlice({
  name: "product",
  initialState: {
    categoryList: [],
    productList: [],
    productDetail: null,
    loading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    //category actions
    builder.addCase(getCategoryList.fulfilled, (state, action) => {
      state.categoryList = action.payload || [];
      state.loading = false;
    });
    builder.addCase(addProductCategory.fulfilled, (state, action) => {
      state.categoryList.unshift(action.payload);
      state.loading = false;
      toast.success("Category added successfully");
    });
    builder.addCase(deleteCategory.fulfilled, (state, { payload }) => {
      state.categoryList = state.categoryList.filter(
        (category) => category._id !== payload
      );
      state.loading = false;
      toast.success("Category deleted successfully");
    });
    builder.addCase(updateProductCategory.fulfilled, (state, { payload }) => {
      state.categoryList = state.categoryList.map((pcl) =>
        pcl._id === payload._id ? payload : pcl
      );
      toast.success("Category successfully updated.");
      state.loading = false;
    });

    //product actions
    builder.addCase(getProductList.fulfilled, (state, action) => {
      state.productList = action.payload || [];
      state.loading = false;
    });
    builder.addCase(getProductDetail.fulfilled, (state, action) => {
      state.productDetail = action.payload;
      state.loading = false;
    });
    builder.addCase(addProduct.fulfilled, (state, action) => {
      state.productList.unshift(action.payload);
      state.loading = false;
      toast.success("Product added successfully");
    });
    builder.addCase(updateProduct.fulfilled, (state, { payload }) => {
      state.productList = state.productList.map((product) =>
        product._id === payload._id ? payload : product
      );
      toast.success("Product successfully updated.");
      state.loading = false;
    });
    builder.addCase(deleteProduct.fulfilled, (state, { payload }) => {
      state.productList = state.productList.filter(
        (product) => product._id !== payload
      );
      state.loading = false;
      toast.success("Product deleted successfully");
    });
    builder.addCase(uploadProductImages.fulfilled, (state, { payload }) => {
      state.productList = state.productList.map((product) =>
        product._id === payload._id ? payload : product
      );
      toast.success("Images uploaded successfully");
      state.loading = false;
    });
    builder.addCase(removeProductImage.fulfilled, (state, { payload }) => {
      state.productList = state.productList.map((product) =>
        product._id === payload._id ? payload : product
      );
      toast.success("Image removed successfully");
      state.loading = false;
    });

    // General Matchers
    builder.addMatcher(
      (action) => action.type.startsWith("product/") && action.type.endsWith("/pending"),
      (state) => { state.loading = true; }
    );
    builder.addMatcher(
      (action) => action.type.startsWith("product/") && (action.type.endsWith("/fulfilled") || action.type.endsWith("/rejected")),
      (state, action) => {
        state.loading = false;
        if (action.type.endsWith("/rejected")) {
          toast.error(action.payload?.message || "Something went wrong");
        }
      }
    );
  },
});

export default productSlice.reducer;
